import React, { useState, useEffect } from 'react';
import { ArrowLeft, ChevronDown, Plus, UploadCloud, X } from 'lucide-react';
import DocumentPreview from '../../../Components/Ui/DocumentPreview';
import Card from '../../../Components/Ui/Card';
import Button from '../../../Components/Ui/Button';
import { useNavigate } from 'react-router-dom';
import toastService from '../../../services/toastService';
import {
  useCreateProductMutation,
  useGetAllCategoriesQuery,
} from '../../../app/services/productApi';
import useFileUpload from '../../../hooks/fileUpload'; // Import your file upload hook
import { useSelector } from 'react-redux';

const AddnewProduct = () => {
  const [images, setImages] = useState([]);
  const [imageUrls, setImageUrls] = useState([]); // Store uploaded image URLs
  const [selectedImage, setSelectedImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    mrp: '',
    description: '',
    category: '',
    subcategory: '',
    sku: '',
    productCode: '',
    packagingType: {
      standard: '',
      master: '',
    },
    tax: {
      hsn_sac: '',
      gstRate: '18',
    },
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useGetAllCategoriesQuery(true);
  const { brandId } = useSelector((state) => state.myBrand);
  const { user } = useSelector((state) => state.userInfo);
  const navigate = useNavigate();
  const [createProduct, { isLoading }] = useCreateProductMutation();
  const { uploadFile, uploading, isCompressing } = useFileUpload(); // Use your file upload hook
  const [keyFeatures, setKeyFeatures] = useState([{ title: '', description: '' }]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);

  const isVerified = user?.status === 'activate';

  useEffect(() => {
    if (!isVerified) {
      toastService.info(
        'Your brand account is pending verification. You will be able to add products once approved by admin.',
        {
          toastId: 'verification-pending',
        },
      );
    }
  }, [isVerified]);

  useEffect(() => {
    if (categoriesData?.data) {
      setCategories(categoriesData.data);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (formData.category) {
      const selectedCategory = categories.find((cat) => cat._id === formData.category);
      setSubcategories(selectedCategory?.children || []);
      setFormData((prev) => ({ ...prev, subcategory: '' }));
    } else {
      setSubcategories([]);
    }
  }, [formData.category, categories]);

  const generateRandomSKU = () => {
    const randomSKU = 'SKU-' + Math.random().toString(36).substring(2, 10).toUpperCase();
    setFormData((prev) => ({ ...prev, sku: randomSKU }));
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const maxFiles = 15;
    const validImages = files.filter((file) => file.size <= 50 * 1024 * 1024);

    //  Alert if some files are skipped due to size
    const invalidImages = files.filter((file) => file.size > 50 * 1024 * 1024);
    if (invalidImages.length > 0) {
      invalidImages.forEach((file) => {
        toastService.error(`${file.name} exceeds 50MB and was not uploaded.`);
      });
    }

    if (images.length + validImages.length <= maxFiles) {
      setImages([...images, ...validImages]);

      // Upload each file immediately
      for (const file of validImages) {
        try {
          const uploadedUrl = await uploadFile(file);
          setImageUrls((prev) => [...prev, uploadedUrl]);
        } catch (error) {
          toastService.error(`Failed to upload ${file.name}`);
        }
      }
    } else {
      toastService.error('You can only upload up to 15 images (50MB each).');
    }
  };

  const removeImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    const updatedUrls = imageUrls.filter((_, i) => i !== index);
    setImages(updatedImages);
    setImageUrls(updatedUrls);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Check if this is a packagingType or tax field
    if (name.startsWith('packagingType.')) {
      const field = name.split('.')[1];
      const roundedValue = Math.round(Number(value));
      setFormData((prev) => ({
        ...prev,
        packagingType: {
          ...prev.packagingType,
          [field]: roundedValue,
        },
      }));
    } else if (name.startsWith('tax.')) {
      const field = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        tax: {
          ...prev.tax,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { title, mrp, description, category, subcategory, sku, productCode, packagingType, tax } =
      formData;

    // Basic validation (HSN/SAC is NOT required)
    if (
      !title.trim() ||
      !mrp.toString().trim() ||
      !description.trim() ||
      !category.trim() ||
      !productCode.trim()
    ) {
      toastService.error('Please fill in all required fields.');
      return;
    }

    // Only validate HSN/SAC length if filled
    if (tax.hsn_sac && (tax.hsn_sac.length < 4 || tax.hsn_sac.length > 8)) {
      toastService.error('HSN/SAC must be between 4 and 8 characters long.');
      return;
    }

    // Validate tax fields
    if (tax.gstRate && (tax.gstRate < 0 || tax.gstRate > 100)) {
      toastService.error('GST Rate must be between 0 and 100.');
      return;
    }

    if (tax.gstin && tax.gstin.length !== 15) {
      toastService.error('GSTIN must be 15 characters long.');
      return;
    }

    // Packaging validation: only check if filled (optional)
    if (formData.packagingType.standard !== '' && isNaN(Number(formData.packagingType.standard))) {
      toastService.error('Packaging type values must be numbers.');
      return;
    }

    if (formData.packagingType.master !== '' && isNaN(Number(formData.packagingType.master))) {
      toastService.error('Master packaging must be a number');
      return;
    }

    if (images.length === 0) {
      toastService.error('Please upload at least one product image.');
      return;
    }

    // Check if all images are uploaded
    if (imageUrls.length !== images.length) {
      toastService.error('Please wait for all images to upload.');
      return;
    }

    try {
      const productData = {
        title,
        brand: brandId,
        category: subcategory || category,
        mrp: parseFloat(mrp),
        packagingType,
        // sku,
        productCode,
        description,
        images: imageUrls, // Use uploaded URLs instead of file names
        keyFeatures: keyFeatures.filter((f) => f.title && f.description),
        tax,
      };
      if (sku && sku.trim() !== '') {
        productData.sku = sku.trim();
      }
      const response = await createProduct(productData).unwrap();

      if (response.success) {
        toastService.success(response.message || 'Product created successfully', {
          toastId: 'product-success',
        });
        // Reset form
        setFormData({
          title: '',
          mrp: '',
          description: '',
          category: '',
          subcategory: '',
          sku: '',
          productCode: '',
          packagingType: {
            standard: '',
            master: '',
          },
        });
        setImages([]);
        setImageUrls([]);
        setKeyFeatures([{ title: '', description: '' }]);
        setTimeout(() => navigate('/brand/products'), 2000);
      }
    } catch (error) {
      let errorMessage = 'Failed to create product';

      if (error.data) {
        errorMessage = error.data.message || errorMessage;
        if (error.data.errors) {
          errorMessage += ': ' + Object.values(error.data.errors).join(', ');
        }
      }

      toastService.error(errorMessage);
    }
  };

  // Add new key feature field
  const addKeyFeature = () => {
    setKeyFeatures([...keyFeatures, { title: '', description: '' }]);
  };

  // Remove key feature field
  const removeKeyFeature = (index) => {
    const updatedFeatures = [...keyFeatures];
    updatedFeatures.splice(index, 1);
    setKeyFeatures(updatedFeatures);
  };

  // Handle key feature change
  const handleKeyFeatureChange = (index, field, value) => {
    const updatedFeatures = [...keyFeatures];
    updatedFeatures[index][field] = value;
    setKeyFeatures(updatedFeatures);
  };

  return (
    <div className="pt-0 px-4 sm:px-6 md:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 justify-center items-center">
        <h2 className="text-lg xl:text-2xl font-bold md:pl-5">Add New Product</h2>
        <Button
          onClick={() => navigate(-1)}
          className="mt-4 sm:mt-0 w-[40%] md:w-[30%] lg:w-[15%] xl:w-[10%]"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
      </div>

      <Card className="mt-2">
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <h3 className="text-lg xl:text-xl font-bold text-primary mb-4 border-b border-gray-200 pb-2">
              Basic Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Product Title */}
              <div>
                <label className="block text-sm font-medium text-secondary">
                  Product Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>

              {/* SKU */}
              <div>
                <label className="block text-sm font-medium text-secondary">SKU</label>
                <div className="flex gap-2 items-center mt-1">
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleInputChange}
                    className="text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                  />
                  <button
                    type="button"
                    onClick={generateRandomSKU}
                    className="p-2 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                    title="Generate Random SKU"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              {/* MRP */}
              <div>
                <label className="block text-sm font-medium text-secondary">
                  MRP (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="mrp"
                  value={formData.mrp}
                  onChange={handleInputChange}
                  required
                  step="0.01"
                  min="0"
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>
              {/* Product Code */}
              <div>
                <label className="block text-sm font-medium text-secondary">
                  Product Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="productCode"
                  value={formData.productCode}
                  onChange={handleInputChange}
                  required
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>
              {/* Packaging Type */}
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary">
                    Package Quantity
                    <span className="text-xs font-normal ml-1 text-gray-500">
                      (Number of units per standard package)
                    </span>
                  </label>
                  <input
                    type="number"
                    name="packagingType.standard"
                    value={formData.packagingType.standard}
                    onChange={handleInputChange}
                    placeholder="Enter quantity"
                    className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary">
                    Master Package Quantity
                    <span className="text-xs font-normal ml-1 text-gray-500">
                      (Number of units per shipping carton)
                    </span>
                  </label>
                  <input
                    type="number"
                    name="packagingType.master"
                    value={formData.packagingType.master}
                    onChange={handleInputChange}
                    placeholder="Enter quantity"
                    className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                  />
                </div>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-secondary">
                  Category <span className="text-red-500">*</span>
                </label>
                <div className="relative mt-1">
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    disabled={categoriesLoading}
                    className="block text-sm w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color pr-10 appearance-none"
                  >
                    <option value="">Select a category</option>
                    {categories
                      .filter((category) => !category.parentCategory)
                      .map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.name}
                        </option>
                      ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Subcategory */}
              <div>
                <label className="block text-sm font-medium text-secondary">Subcategory</label>
                <div className="relative mt-1">
                  <select
                    name="subcategory"
                    value={formData.subcategory}
                    onChange={handleInputChange}
                    disabled={!formData.category || subcategories.length === 0}
                    className="block text-sm w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color pr-10 appearance-none"
                  >
                    <option value="">
                      {subcategories.length ? 'Select a subcategory' : 'No subcategories available'}
                    </option>
                    {subcategories.map((subcategory) => (
                      <option key={subcategory._id} value={subcategory._id}>
                        {subcategory.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                    <ChevronDown size={16} className="text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-secondary">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  rows={4}
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>
            </div>
          </div>

          {/* Tax Details */}
          <div>
            <h3 className="text-lg xl:text-xl font-bold text-primary mb-4 border-b border-gray-200 pb-2">
              Tax Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* HSN/SAC */}
              <div>
                <label className="block text-sm font-medium text-secondary">HSN</label>
                <input
                  type="text"
                  name="tax.hsn_sac"
                  value={formData.tax?.hsn_sac || ''}
                  onChange={handleInputChange}
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>

              {/* GST Rate */}
              <div>
                <label className="block text-sm font-semibold text-secondary">GST Rate (%)</label>
                <input
                  type="number"
                  name="tax.gstRate"
                  value={formData.tax?.gstRate || ''}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  step="0.01"
                  className="mt-1 text-sm block w-full rounded-md py-2 px-3 border border-gray-300 shadow-sm focus:outline-none focus:ring-2 border-color"
                />
              </div>
            </div>
          </div>

          {/* Key Features - Updated to horizontal layout */}
          <div>
            <h3 className="text-lg xl:text-xl font-bold text-primary mb-4 border-b border-gray-200 pb-2">
              Key Features
            </h3>
            <div className="space-y-4">
              {keyFeatures.map((feature, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50"
                >
                  {/* Left side - Feature Title */}
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">Title</label>
                    <input
                      type="text"
                      placeholder="Eg. color"
                      value={feature.title}
                      onChange={(e) => handleKeyFeatureChange(index, 'title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 border-color bg-white"
                    />
                  </div>

                  {/* Right side - Feature Description + Remove Button */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-secondary mb-2">
                        Description
                      </label>
                      <input
                        type="text"
                        placeholder="white"
                        value={feature.description}
                        onChange={(e) =>
                          handleKeyFeatureChange(index, 'description', e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 border-color bg-white"
                      />
                    </div>
                    <div className="flex items-end pb-2">
                      <button
                        type="button"
                        onClick={() => removeKeyFeature(index)}
                        className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-md transition-colors duration-200"
                        title="Remove feature"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <Button type="button" onClick={addKeyFeature}>
                Add Feature
              </Button>
            </div>
          </div>

          {/* Product Images */}
          <div>
            <h3 className="text-lg xl:text-xl font-bold text-primary mb-4 border-b border-gray-200 pb-2">
              Product Images <span className="text-red-500">*</span>
            </h3>
            <label className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer">
              <UploadCloud size={36} className="text-gray-400 mb-2" />
              <p className="text-sm font-medium text-secondary mb-1">
                Drag & drop product images here
              </p>
              <p className="text-xs text-gray-500 mb-4">Support for JPG, PNG.</p>
              {uploading && (
                <p className="text-xs text-blue-600 mb-2">
                  {isCompressing ? 'Compressing image...' : 'Uploading images...'}
                </p>
              )}
              <div className="inline-block mt-2 px-3 py-1 primary-background white-text rounded-md shadow-sm text-sm lg:text-base">
                Select Files
              </div>
              <input
                type="file"
                accept=".jpg,.jpeg,.png"
                multiple
                onChange={handleImageChange}
                className="hidden"
                disabled={uploading}
              />
            </label>

            {images.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {images.map((img, i) => (
                  <div key={i} className="relative" style={{ width: 150, height: 100 }}>
                    <img
                      src={imageUrls[i] ? imageUrls[i] : URL.createObjectURL(img)}
                      alt={`Preview ${i + 1}`}
                      className="object-cover w-full h-full cursor-pointer rounded border"
                      style={{ width: 100, height: 100 }}
                      onClick={() =>
                        setSelectedImage(imageUrls[i] ? imageUrls[i] : URL.createObjectURL(img))
                      }
                    />
                    {/* Show upload status */}
                    {imageUrls[i] ? (
                      <div className="absolute top-1 left-1 bg-green-500 white-text text-xs px-1 py-0.5 rounded">
                        ✓
                      </div>
                    ) : (
                      <div className="absolute top-1 left-1 bg-blue-500 white-text text-xs px-1 py-0.5 rounded">
                        ↑
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 bg-white text-gray-600 hover:text-primary rounded-full p-1 shadow-sm"
                      disabled={uploading}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-col items-end space-y-2">
            {!isVerified && (
              <p className="text-sm text-red-500 font-medium">
                Your account is pending verification by Admin.
              </p>
            )}
            <div className="flex justify-end space-x-4">
              <Button
                className="text-sm"
                type="submit"
                disabled={
                  !isVerified ||
                  isLoading ||
                  uploading ||
                  isCompressing ||
                  (images.length > 0 && imageUrls.length !== images.length)
                }
              >
                {!isVerified
                  ? 'Verification Pending'
                  : isLoading
                    ? 'Creating...'
                    : uploading
                      ? 'Uploading...'
                      : 'Create Product'}
              </Button>
            </div>
          </div>
        </form>
        <DocumentPreview
          isOpen={!!selectedImage}
          fileUrl={selectedImage}
          onClose={() => setSelectedImage(null)}
          fileType={selectedImage ? selectedImage.split('.').pop() : ''}
        />
      </Card>
    </div>
  );
};

export default AddnewProduct;
